import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * RL-473 — integration profile 2 sources. Back Office stores them and hands
 * them to the Link of their store; it never runs them, so there is no import
 * status here and no run button anywhere.
 */

/** The profile's settings as Back Office stores them. Never the password. */
export type Ip2Settings = {
  server: string;
  database: string;
  hierarchyCatalogCode: string;
  departmentCode: string;
  languageId: number;
  currency: string;
  unitMap: Record<string, string>;
  connectTimeoutSeconds: number;
  commandTimeoutSeconds: number;
};

export type Ip2Source = {
  id: string;
  profile: 'ip-2';
  name: string;
  username: string;
  enabled: boolean;
  settings: Ip2Settings;
  updatedAt: string;
  location: { id: string; code: string; name: string };
};

export type Ip2SourceInput = {
  locationId: string;
  name: string;
  username: string;
  password: string;
  settings: Omit<Ip2Settings, 'connectTimeoutSeconds' | 'commandTimeoutSeconds'>;
};

/** An edit. `password` absent keeps the stored one; it cannot be read back. */
export type Ip2SourceChange = {
  name?: string;
  username?: string;
  password?: string;
  enabled?: boolean;
  settings?: Partial<Ip2Settings>;
};

const KEY = ['ip2-sources'] as const;
const PATH = '/connectors/ip-2/sources';

export function useIp2Sources(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async (): Promise<Ip2Source[]> => {
      const response = await apiClient.get<Ip2Source[]>(PATH);
      return response.data;
    },
  });
}

export function useCreateIp2Source() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Ip2SourceInput) => {
      await apiClient.post(PATH, input);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateIp2Source() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sourceId: string; change: Ip2SourceChange }) => {
      await apiClient.patch(`${PATH}/${input.sourceId}`, input.change);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
